import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Title,
  Table,
  Button,
  Paper,
  Group,
  Text,
  Badge,
  Loader,
  Stack,
  Tabs,
  Alert,
  Card,
  Progress,
  Select,
  Tooltip,
  Pagination,
  TextInput,
  Checkbox,
  SimpleGrid,
  SegmentedControl,
  Modal,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";
import {
  IconRefresh,
  IconAlertCircle,
  IconRobot,
  IconBarcode,
  IconCheck,
  IconFilter,
  IconPrinter,
  IconDownload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useStainingSlides,
  useStainingStats,
  useVendorProgress,
  useCompleteNonIHCStainingScan,
  useBulkCompleteNonIHCStaining,
  useManualCompleteIHCStaining,
  useSetSlideStainerType,
  useResendIhcSlide,
} from "../../hooks/useWorkflows";
import { FilterModal, ActiveFilters } from "../../components/cases";
import { DeleteStainButton } from "../../components/workflows";
import { IhcResendButton } from "../../components/workflows/IhcResendButton";
import { StainingStatusBadge } from "../../components/workflows/StainingStatusBadge";
import { type CaseFilters } from "../../services";
import {
  getBiocarePreflightFailure,
  type BiocarePreflightFailure,
  type StainingCounts,
} from "../../services/workflows.service";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import {
  formatDateOnlyForDisplay,
  getLabTodayDateOnly,
  parseDateOnly,
} from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";
import { isMachineTrackedStainType } from "../../utils/stainType";

const ITEMS_PER_PAGE = 20;

const LAB_INSTANT_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

// Counts placeholder used until the first response arrives, so badges and
// pagination render as zero rather than crashing on undefined.
const EMPTY_STAINING_COUNTS: StainingCounts = {
  all: { queue: 0, completed: 0 },
  ihc: { queue: 0, completed: 0 },
  nonIhc: { queue: 0, completed: 0 },
};

const STAIN_TYPE_COLORS: Record<string, string> = {
  "H&E": "blue",
  IHC: "green",
  ISH: "violet",
  Special: "orange",
  Other: "gray",
};

// The only stainer choice on the staining page: which vendor the slide goes to.
// Drives the HL7 dialect; the order routes to the one designated Staining Hub.
const STAINER_OPTIONS = [
  { value: "biocare", label: "Biocare" },
  { value: "leica", label: "Leica" },
];

function formatLabInstant(value: string | Date): string {
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isNaN(instant.getTime())
    ? "N/A"
    : LAB_INSTANT_FORMATTER.format(instant);
}

function filtersToParams(f: CaseFilters): Record<string, string | null> {
  return {
    search: f.search || null,
    facilityIds: f.facilityIds?.length ? f.facilityIds.join(",") : null,
    clientId: f.clientId || null,
    pathologistIds: f.pathologistIds?.length
      ? f.pathologistIds.join(",")
      : null,
    currentStatusIds: f.currentStatusIds?.length
      ? f.currentStatusIds.join(",")
      : null,
    workflowIds: f.workflowIds?.length ? f.workflowIds.join(",") : null,
    procedureIds: f.procedureIds?.length ? f.procedureIds.join(",") : null,
    patientIds: f.patientIds?.length ? f.patientIds.join(",") : null,
    dateFrom: parseDateOnly(f.dateFrom) ?? null,
    dateTo: parseDateOnly(f.dateTo) ?? null,
    isBlocked: f.isBlocked ? "1" : null,
    isRush: f.isRush ? "1" : null,
    isHighPriority: f.isHighPriority ? "1" : null,
    flagIds: f.flagDefinitionIds?.length ? f.flagDefinitionIds.join(",") : null,
    caseType: f.caseType || null,
    tatOverdue: f.tatOverdue ? "1" : null,
    tatMinDays: f.tatMinDays != null ? String(f.tatMinDays) : null,
    hasPendingTests: f.hasPendingTests ? "1" : null,
    currentStep: f.currentStep || null,
    eligibleForSendOut: f.eligibleForSendOut ? "1" : null,
  };
}

function paramsToFilters(searchParams: URLSearchParams): CaseFilters {
  const dateFrom = parseDateOnly(searchParams.get("dateFrom"));
  const dateTo = parseDateOnly(searchParams.get("dateTo"));

  return {
    search: searchParams.get("search") || undefined,
    facilityIds:
      searchParams.get("facilityIds")?.split(",").filter(Boolean) || undefined,
    clientId: searchParams.get("clientId") || undefined,
    pathologistIds:
      searchParams.get("pathologistIds")?.split(",").filter(Boolean) ||
      undefined,
    currentStatusIds:
      searchParams.get("currentStatusIds")?.split(",").filter(Boolean) ||
      undefined,
    workflowIds:
      searchParams.get("workflowIds")?.split(",").filter(Boolean) || undefined,
    procedureIds:
      searchParams.get("procedureIds")?.split(",").filter(Boolean) || undefined,
    patientIds:
      searchParams.get("patientIds")?.split(",").filter(Boolean) || undefined,
    dateFrom,
    dateTo,
    isBlocked: searchParams.get("isBlocked") === "1" ? true : undefined,
    isRush: searchParams.get("isRush") === "1" ? true : undefined,
    isHighPriority:
      searchParams.get("isHighPriority") === "1" ? true : undefined,
    flagDefinitionIds:
      searchParams.get("flagIds")?.split(",").filter(Boolean) || undefined,
    caseType: searchParams.get("caseType") || undefined,
    tatOverdue: searchParams.get("tatOverdue") === "1" ? true : undefined,
    tatMinDays: searchParams.get("tatMinDays")
      ? parseInt(searchParams.get("tatMinDays")!, 10)
      : undefined,
    hasPendingTests:
      searchParams.get("hasPendingTests") === "1" ? true : undefined,
    currentStep: searchParams.get("currentStep") || undefined,
    eligibleForSendOut:
      searchParams.get("eligibleForSendOut") === "1" ? true : undefined,
  };
}

export default function StainingQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canManuallyCompleteStaining = useHasPermission("staining.write");
  const canDeleteStain = useHasPermission("stains.delete");

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const filter = useMemo<"all" | "ihc" | "non-ihc">(() => {
    const cat = searchParams.get("cat");
    return cat === "ihc" || cat === "non-ihc" ? cat : "all";
  }, [searchParams]);

  const view = useMemo<"queue" | "completed">(
    () => (searchParams.get("view") === "completed" ? "completed" : "queue"),
    [searchParams]
  );

  // Manual completion is IHC-only; deletion applies to every tab. The Actions
  // column header and cells must share one condition or they desynchronise.
  const canCompleteRowActions = useMemo(
    () => filter !== "non-ihc" && canManuallyCompleteStaining,
    [filter, canManuallyCompleteStaining]
  );
  const showRowActions = useMemo(
    () =>
      canCompleteRowActions ||
      canDeleteStain ||
      (filter === "ihc" && canManuallyCompleteStaining),
    [canCompleteRowActions, canDeleteStain, filter, canManuallyCompleteStaining]
  );

  const page = useMemo(
    () => Math.max(1, parseInt(searchParams.get("qp") ?? "1", 10) || 1),
    [searchParams]
  );

  const completedPage = useMemo(
    () => Math.max(1, parseInt(searchParams.get("cp") ?? "1", 10) || 1),
    [searchParams]
  );

  const caseFilters = useMemo<CaseFilters>(
    () => paramsToFilters(searchParams),
    [searchParams]
  );

  const [barcodeInput, setBarcodeInput] = useState("");
  const [selectedSlides, setSelectedSlides] = useState<Set<string>>(new Set());
  const [manualCompletionTarget, setManualCompletionTarget] = useState<
    any | null
  >(null);
  const [manualCompletionNotes, setManualCompletionNotes] = useState("");
  const [biocarePreflightFailure, setBiocarePreflightFailure] =
    useState<BiocarePreflightFailure>();
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const manualCompletionInFlightRef = useRef(false);
  const resendInFlightRef = useRef<Set<string>>(new Set());
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  // Single server-paginated fetch: only the visible page of the active
  // (category, view) plus queue/completed counts for every tab badge.
  const activePage = view === "queue" ? page : completedPage;
  const {
    data: slidesResponse,
    isLoading,
    isFetching,
    refetch,
  } = useStainingSlides({
    category: filter,
    view,
    page: activePage,
    limit: ITEMS_PER_PAGE,
    filters: caseFilters,
  });

  const rows = useMemo<any[]>(
    () => slidesResponse?.data ?? [],
    [slidesResponse]
  );

  const biocareErrorBySlide = useMemo(
    () =>
      new Map(
        biocarePreflightFailure?.errors.map((error) => [
          error.slideId,
          error.message,
        ]) ?? []
      ),
    [biocarePreflightFailure]
  );

  const handlePrintWorklist = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const generatedAt = new Date();
    const labDate = getLabTodayDateOnly(generatedAt);

    const title = `Staining Worklist - ${
      filter === "ihc" ? "IHC/ISH" : filter === "non-ihc" ? "Non-IHC" : "All"
    } - ${formatDateOnlyForDisplay(labDate)}`;

    let html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; }
            h2 { margin-bottom: 5px; }
            .meta { font-size: 14px; color: #666; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <div class="meta">Generated on ${formatLabInstant(generatedAt)} | ${
      rows.length
    } slides</div>
          <table>
            <thead>
              <tr>
                <th>Case Number</th>
                <th>Part</th>
                <th>Block</th>
                <th>Slide</th>
                <th>Stain</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
    `;

    rows.forEach((slide: any) => {
      html += `
        <tr>
          <td>${getDisplayCaseNumber(slide.case) || "N/A"}</td>
          <td>${slide.specimen?.number || "N/A"}</td>
          <td>${slide.block?.number || "N/A"}</td>
          <td>${getPhysicalSlideNumber(slide)}</td>
          <td>${slide.stain?.code || "N/A"}</td>
          <td>${slide.stain?.stainType || "N/A"}</td>
          <td>${
            slide.stainingPriority > 0 ? slide.stainingPriority : "Normal"
          }</td>
          <td>${slide.requestReason || ""}</td>
        </tr>
      `;
    });

    html += `
            </tbody>
          </table>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  }, [filter, rows]);

  const handleExportCSV = useCallback(() => {
    const generatedAt = new Date();
    const labDate = getLabTodayDateOnly(generatedAt);
    const headers = [
      "Case Number",
      "Part",
      "Block",
      "Slide",
      "Stain",
      "Type",
      "Priority",
      "Remarks",
    ];
    const csvRows = rows.map((slide: any) => [
      getDisplayCaseNumber(slide.case) || "N/A",
      slide.specimen?.number || "N/A",
      slide.block?.number || "N/A",
      getPhysicalSlideNumber(slide),
      slide.stain?.code || "N/A",
      slide.stain?.stainType || "N/A",
      slide.stainingPriority > 0 ? slide.stainingPriority : "Normal",
      `"${(slide.requestReason || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...csvRows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `staining_worklist_${labDate}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows]);

  const { data: stats } = useStainingStats();
  const { data: vendorProgress } = useVendorProgress();

  const scanMutation = useCompleteNonIHCStainingScan();
  const bulkCompleteMutation = useBulkCompleteNonIHCStaining();
  const manualCompleteMutation = useManualCompleteIHCStaining();
  const stainerTypeMutation = useSetSlideStainerType();
  const resendMutation = useResendIhcSlide();

  // Check if any case filters are active
  const hasActiveFilters = useMemo(
    () =>
      Object.values(caseFilters).some((value) => {
        if (value === undefined || value === null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.length > 0;
        if (typeof value === "boolean") return value;
        return !!value;
      }),
    [caseFilters]
  );

  const handleFiltersApply = useCallback(
    (newFilters: CaseFilters) => {
      updateParams({ ...filtersToParams(newFilters), qp: null, cp: null });
    },
    [updateParams]
  );

  const handleClearAllFilters = useCallback(() => {
    updateParams({ ...filtersToParams({}), qp: null, cp: null });
  }, [updateParams]);

  const handleCategoryChange = useCallback(
    (value: string | null) => {
      setSelectedSlides(new Set());
      updateParams({
        cat: value === "ihc" || value === "non-ihc" ? value : null,
        qp: null,
        cp: null,
      });
    },
    [updateParams]
  );

  const handleViewChange = useCallback(
    (value: string) => {
      updateParams({ view: value === "completed" ? "completed" : null });
    },
    [updateParams]
  );

  const handleQueuePageChange = useCallback(
    (value: number) => {
      updateParams({ qp: value > 1 ? String(value) : null });
    },
    [updateParams]
  );

  const handleCompletedPageChange = useCallback(
    (value: number) => {
      updateParams({ cp: value > 1 ? String(value) : null });
    },
    [updateParams]
  );

  const handleVendorChange = useCallback(
    (value: string | null) => {
      updateParams({ vendor: value });
    },
    [updateParams]
  );

  const handleBarcodeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setBarcodeInput(event.currentTarget.value);
    },
    []
  );

  // Queue/completed counts for every category, returned on every response
  // regardless of the active view. Drives the tab and segment badges.
  const counts = useMemo<StainingCounts>(
    () => slidesResponse?.counts ?? EMPTY_STAINING_COUNTS,
    [slidesResponse]
  );

  // Tab badges show the per-category queue count (pending, not completed).
  const tabCounts = useMemo(
    () => ({
      all: counts.all.queue,
      ihc: counts.ihc.queue,
      nonIhc: counts.nonIhc.queue,
    }),
    [counts]
  );

  // Counts for the currently selected category, driving the Queue/Completed
  // segment labels, pagination totals and empty states.
  const currentCounts = useMemo(
    () =>
      filter === "ihc"
        ? counts.ihc
        : filter === "non-ihc"
        ? counts.nonIhc
        : counts.all,
    [counts, filter]
  );

  const totalPages = useMemo(
    () => Math.ceil(currentCounts.queue / ITEMS_PER_PAGE),
    [currentCounts.queue]
  );

  const totalCompletedPages = useMemo(
    () => Math.ceil(currentCounts.completed / ITEMS_PER_PAGE),
    [currentCounts.completed]
  );

  // Auto-clamp page if current page exceeds total pages (e.g. after data shrinks)
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      updateParams({ qp: totalPages > 1 ? String(totalPages) : null });
    }
  }, [page, totalPages, updateParams]);

  useEffect(() => {
    if (completedPage > totalCompletedPages && totalCompletedPages > 0) {
      updateParams({
        cp: totalCompletedPages > 1 ? String(totalCompletedPages) : null,
      });
    }
  }, [completedPage, totalCompletedPages, updateParams]);

  // Selected vendor is tracked in the `vendor` URL param; fall back to the first
  // vendor for display so the card is populated before any explicit selection.
  const selectedVendor = useMemo(() => {
    if (!vendorProgress || vendorProgress.length === 0) return undefined;
    const vendorParam = searchParams.get("vendor");
    return (
      vendorProgress.find((v) => v.vendorId === vendorParam) ??
      vendorProgress[0]
    );
  }, [vendorProgress, searchParams]);

  const vendorProgressPercent = useMemo(
    () =>
      selectedVendor
        ? Math.min(
            100,
            Math.round((selectedVendor.sent / selectedVendor.minimum) * 100)
          )
        : 0,
    [selectedVendor]
  );

  // Non-IHC staining completion handlers
  const handleBarcodeScan = async () => {
    if (!barcodeInput.trim()) return;

    try {
      const result = await scanMutation.mutateAsync(barcodeInput.trim());
      setBarcodeInput("");

      if (result.alreadyCompleted) {
        notifications.show({
          title: "Already Completed",
          message: "This slide was already marked as staining completed",
          color: "yellow",
        });
      } else {
        notifications.show({
          title: "Staining Complete",
          message: `Slide marked as staining completed`,
          color: "green",
        });
      }

      if (result.caseReady) {
        notifications.show({
          title: "Case Ready",
          message: "All slides for this case are now ready for distribution",
          color: "blue",
        });
      }

      refetch();
    } catch (error: any) {
      notifications.show({
        title: "Scan Error",
        message: error.message || "Failed to complete staining",
        color: "red",
      });
    }
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBarcodeScan();
    }
  };

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        setBarcodeInput(barcode);
        try {
          const result = await scanMutation.mutateAsync(barcode);
          setBarcodeInput("");

          if (result.alreadyCompleted) {
            notifications.show({
              title: "Already Completed",
              message: "This slide was already marked as staining completed",
              color: "yellow",
            });
          } else {
            notifications.show({
              title: "Staining Complete",
              message: `Slide marked as staining completed`,
              color: "green",
            });
          }

          if (result.caseReady) {
            notifications.show({
              title: "Case Ready",
              message:
                "All slides for this case are now ready for distribution",
              color: "blue",
            });
          }

          refetch();
        } catch (error: any) {
          notifications.show({
            title: "Scan Error",
            message: error.message || "Failed to complete staining",
            color: "red",
          });
        }
      },
      [scanMutation, refetch]
    )
  );

  const handleBulkComplete = async () => {
    if (selectedSlides.size === 0) {
      notifications.show({
        title: "No Selection",
        message: "Please select slides to complete",
        color: "blue",
      });
      return;
    }

    try {
      const result = await bulkCompleteMutation.mutateAsync(
        Array.from(selectedSlides)
      );

      notifications.show({
        title: "Staining Complete",
        message: `Completed ${result.completed} slides (${result.alreadyCompleted} already done, ${result.failed} failed)`,
        color: result.failed > 0 ? "yellow" : "green",
      });

      if (result.casesReady.length > 0) {
        notifications.show({
          title: "Cases Ready",
          message: `${result.casesReady.length} case(s) now ready for distribution`,
          color: "blue",
        });
      }

      setSelectedSlides(new Set());
      refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to complete staining",
        color: "red",
      });
    }
  };

  const handleSelectAll = () => {
    if (selectedSlides.size === rows.length) {
      setSelectedSlides(new Set());
    } else {
      setSelectedSlides(new Set(rows.map((s: any) => s.id)));
    }
  };

  const toggleSlideSelection = (slideId: string) => {
    const newSet = new Set(selectedSlides);
    if (newSet.has(slideId)) {
      newSet.delete(slideId);
    } else {
      newSet.add(slideId);
    }
    setSelectedSlides(newSet);
  };

  const handleStainerChange = (slideId: string, stainerType: string | null) => {
    stainerTypeMutation.mutate({
      slideId,
      stainerType: stainerType as "biocare" | "leica" | null,
    });
  };

  const handleResendIhcSlide = useCallback(
    async (slideId: string) => {
      if (resendInFlightRef.current.has(slideId)) return;
      resendInFlightRef.current.add(slideId);
      try {
        await resendMutation.mutateAsync(slideId);
        notifications.show({
          title: "Send started",
          message: "The slide is being sent to the selected stainer.",
          color: "blue",
        });
      } catch (error: unknown) {
        const preflightFailure = getBiocarePreflightFailure(error);
        if (preflightFailure) setBiocarePreflightFailure(preflightFailure);
        notifications.show({
          title: "Resend failed",
          message:
            preflightFailure?.message || "The slide could not be resent.",
          color: "red",
        });
      } finally {
        resendInFlightRef.current.delete(slideId);
      }
    },
    [resendMutation]
  );

  const isManualMachineCompletionEligible = useCallback(
    (slide: any) =>
      isMachineTrackedStainType(slide?.stain?.stainType) &&
      ["ready_for_staining", "staining_in_progress"].includes(slide.status),
    []
  );

  const openManualCompletion = useCallback(
    (slide: any) => {
      if (
        !canManuallyCompleteStaining ||
        !isManualMachineCompletionEligible(slide)
      )
        return;
      setManualCompletionTarget(slide);
      setManualCompletionNotes("");
    },
    [canManuallyCompleteStaining, isManualMachineCompletionEligible]
  );

  const closeManualCompletion = useCallback(() => {
    if (manualCompleteMutation.isPending) return;
    setManualCompletionTarget(null);
    setManualCompletionNotes("");
  }, [manualCompleteMutation.isPending]);

  const handleManualCompletionConfirm = useCallback(async () => {
    if (!manualCompletionTarget || manualCompletionInFlightRef.current) return;
    manualCompletionInFlightRef.current = true;

    try {
      const result = await manualCompleteMutation.mutateAsync({
        slideId: manualCompletionTarget.id,
        caseId: manualCompletionTarget.caseId,
        notes: manualCompletionNotes,
      });

      if (result.alreadyProcessed) {
        notifications.show({
          title: "Already Completed",
          message:
            "This IHC/ISH slide was already marked as staining completed.",
          color: "yellow",
        });
      } else {
        notifications.show({
          title: "Staining Complete",
          message: result.caseAdvanced
            ? "IHC/ISH slide completed manually. The case moved to Pending Distribution."
            : "IHC/ISH slide completed manually.",
          color: "green",
        });
      }

      setManualCompletionTarget(null);
      setManualCompletionNotes("");
    } catch (error: any) {
      notifications.show({
        title: "Manual Completion Failed",
        message: error.message || "Failed to complete IHC/ISH staining",
        color: "red",
      });
    } finally {
      manualCompletionInFlightRef.current = false;
    }
  }, [manualCompleteMutation, manualCompletionNotes, manualCompletionTarget]);

  const getStainerLabel = (stainerType: string | null | undefined) =>
    stainerType === "leica"
      ? "Leica"
      : stainerType === "biocare"
      ? "Biocare"
      : "Stainer";

  const getStatusIndicator = (slide: any) => {
    const queue = slide.ihcQueue;

    if (queue?.dispatchAttemptId && !queue?.processedAt) {
      return (
        <Badge size="xs" color="cyan" variant="light">
          Sending to {getStainerLabel(queue.inFlightStainerType)}
        </Badge>
      );
    }

    // "Failed" is driven by the queue STATUS, not by leftover error text — a
    // distributed slide with a stale error_message must not read as failed.
    if (queue?.status === "failed") {
      const errorMessage = queue?.errorMessage || slide.lastStainingError;
      return (
        <Tooltip label={errorMessage || "Staining failed"} multiline w={300}>
          <Badge size="xs" color="red" style={{ cursor: "help" }}>
            Failed
          </Badge>
        </Tooltip>
      );
    }
    if (queue?.status === "distributed" && !queue?.processedAt) {
      return (
        <Badge size="xs" color="cyan" variant="light">
          Sent to {getStainerLabel(queue.lastSuccessfulStainerType)}
        </Badge>
      );
    }
    if (
      slide.status === "staining_in_progress" ||
      queue?.status === "processing"
    ) {
      return (
        <Badge size="xs" color="blue" variant="light">
          Staining
        </Badge>
      );
    }
    if (!slide.stainerType && queue?.status === "pending") {
      return (
        <Badge size="xs" color="yellow" variant="light">
          Queued
        </Badge>
      );
    }
    return null;
  };

  const getMachineCell = (slide: any) => {
    const isMachineTracked = isMachineTrackedStainType(slide.stain?.stainType);

    if (!isMachineTracked) {
      return (
        <Text size="sm" c="dimmed">
          Manual
        </Text>
      );
    }

    const statusIndicator = getStatusIndicator(slide);

    if (slide.status === "staining_completed") {
      return (
        <Badge color="green" variant="light">
          {getStainerLabel(slide.stainerType)} - Done
        </Badge>
      );
    }

    return (
      <Stack gap={4}>
        <Select
          size="xs"
          w={140}
          placeholder="Select stainer"
          value={slide.stainerType || null}
          onChange={(val) => handleStainerChange(slide.id, val)}
          data={STAINER_OPTIONS}
          clearable
          allowDeselect
          disabled={stainerTypeMutation.isPending}
        />
        {statusIndicator}
      </Stack>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Staining Queue</Title>
          <Group>
            <Button
              leftSection={<IconFilter size={16} />}
              variant={hasActiveFilters ? "filled" : "light"}
              onClick={openFilterModal}
            >
              {hasActiveFilters ? "Edit Filters" : "Add Filters"}
            </Button>
            <Button
              leftSection={<IconPrinter size={16} />}
              variant="light"
              onClick={handlePrintWorklist}
              disabled={rows.length === 0}
            >
              Print Worklist
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              onClick={handleExportCSV}
              disabled={rows.length === 0}
            >
              Export CSV
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={() => refetch()}
              loading={isFetching}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Group justify="flex-end">
            <ActiveFilters
              filters={caseFilters}
              onFiltersChange={handleFiltersApply}
              onClearAll={handleClearAllFilters}
            />
          </Group>
        )}

        <Tabs value={filter} onChange={handleCategoryChange}>
          <Tabs.List>
            <Tabs.Tab value="all">All Slides ({tabCounts.all})</Tabs.Tab>
            <Tabs.Tab value="ihc">IHC/ISH ({tabCounts.ihc})</Tabs.Tab>
            <Tabs.Tab value="non-ihc">
              Non-IHC Slides ({tabCounts.nonIhc})
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Group>
          <SegmentedControl
            value={view}
            onChange={handleViewChange}
            data={[
              { label: `Queue (${currentCounts.queue})`, value: "queue" },
              {
                label: `Completed (${currentCounts.completed})`,
                value: "completed",
              },
            ]}
          />
        </Group>

        {/* IHC Cards - only show on IHC tab */}
        {filter === "ihc" && (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {/* Automatic IHC Distribution */}
            <Card withBorder padding="md">
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <IconRobot size={20} />
                  <Text fw={500}>Automatic IHC/ISH Distribution</Text>
                </Group>
              </Group>
              <Text size="sm" c="dimmed" mb="md">
                Ready IHC/ISH slides are sent to Leica automatically. Change a
                row's stainer and use Resend before staining starts. Resending
                does not cancel the order on the original stainer.
              </Text>
              {biocarePreflightFailure && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertCircle size={16} />}
                  title="Physician information required"
                  mb="md"
                >
                  {biocarePreflightFailure.message}
                </Alert>
              )}
              <Group gap="xl">
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Distributed (24h):
                  </Text>
                  <Text size="sm" fw={500}>
                    {stats?.distributed24h ?? "-"}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Pending:
                  </Text>
                  <Text size="sm" fw={500}>
                    {stats?.pending ?? "-"}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Failed:
                  </Text>
                  <Text
                    size="sm"
                    fw={500}
                    c={stats?.failed ? "red" : undefined}
                  >
                    {stats?.failed ?? "-"}
                  </Text>
                </Group>
              </Group>
            </Card>

            {/* Vendor Daily Progress */}
            {vendorProgress && vendorProgress.length > 0 && (
              <Card withBorder padding="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={500}>Stainer Daily Progress</Text>
                  <Select
                    size="xs"
                    w={200}
                    value={selectedVendor?.vendorId ?? null}
                    onChange={handleVendorChange}
                    data={vendorProgress.map((v) => ({
                      value: v.vendorId,
                      label: v.vendorName,
                    }))}
                  />
                </Group>
                {selectedVendor && (
                  <>
                    <Group gap="xl" mb="sm">
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Minimum:
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedVendor.minimum}
                        </Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Sent:
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedVendor.sent}
                        </Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Completed:
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedVendor.completed}
                        </Text>
                      </Group>
                    </Group>
                    <Progress
                      value={vendorProgressPercent}
                      size="lg"
                      color={vendorProgressPercent >= 100 ? "green" : "blue"}
                    />
                    <Text size="xs" c="dimmed" ta="right" mt={4}>
                      {vendorProgressPercent}% of daily minimum
                    </Text>
                  </>
                )}
              </Card>
            )}
          </SimpleGrid>
        )}

        {/* Non-IHC Staining Completion - only show on Non-IHC Queue tab when there are slides in queue */}
        {filter === "non-ihc" && view === "queue" && tabCounts.nonIhc > 0 && (
          <Card withBorder padding="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="xs">
                  <IconBarcode size={20} />
                  <Text fw={500}>Complete Staining</Text>
                </Group>
                {selectedSlides.size > 0 && (
                  <Button
                    leftSection={<IconCheck size={16} />}
                    onClick={handleBulkComplete}
                    loading={bulkCompleteMutation.isPending}
                  >
                    Complete Selected ({selectedSlides.size})
                  </Button>
                )}
              </Group>
              <Group>
                <TextInput
                  ref={barcodeInputRef}
                  placeholder="Scan slide barcode (e.g., G26-00808;A;1;3)"
                  value={barcodeInput}
                  onChange={handleBarcodeInputChange}
                  onKeyPress={handleBarcodeKeyPress}
                  style={{ flex: 1 }}
                  autoFocus={filter === "non-ihc" && view === "queue"}
                />
                <Button
                  onClick={handleBarcodeScan}
                  loading={scanMutation.isPending}
                  disabled={!barcodeInput.trim()}
                >
                  Scan
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                Scan slide barcodes as they come out of the stainer, or select
                multiple slides and click "Complete Selected"
              </Text>
            </Stack>
          </Card>
        )}

        {view === "queue" &&
          (isLoading ? (
            <Loader />
          ) : currentCounts.queue === 0 ? (
            <Alert icon={<IconAlertCircle size={16} />} title="Queue Empty">
              No pending{" "}
              {filter === "ihc"
                ? "IHC/ISH "
                : filter === "non-ihc"
                ? "Non-IHC "
                : ""}
              slides awaiting staining
            </Alert>
          ) : (
            <Paper withBorder p="md">
              <Stack gap="md">
                {/* Pagination above table */}
                <Group justify="space-between">
                  <Text fw={500} size="lg">
                    Queue ({currentCounts.queue})
                  </Text>
                  {totalPages > 1 && (
                    <Group gap="md">
                      <Text size="sm" c="dimmed">
                        Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                        {Math.min(page * ITEMS_PER_PAGE, currentCounts.queue)}{" "}
                        of {currentCounts.queue} slides
                      </Text>
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={handleQueuePageChange}
                        size="sm"
                      />
                    </Group>
                  )}
                </Group>

                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      {/* Checkbox column: show on Non-IHC tab only */}
                      {filter === "non-ihc" && (
                        <Table.Th w={50}>
                          <Checkbox
                            checked={
                              rows.length > 0 &&
                              selectedSlides.size === rows.length
                            }
                            indeterminate={
                              selectedSlides.size > 0 &&
                              selectedSlides.size < rows.length
                            }
                            onChange={handleSelectAll}
                          />
                        </Table.Th>
                      )}
                      <Table.Th>Case Number</Table.Th>
                      <Table.Th>Part</Table.Th>
                      <Table.Th>Block</Table.Th>
                      <Table.Th>Slide</Table.Th>
                      <Table.Th>Stain</Table.Th>
                      {/* Type column: show on All tab only (IHC tab is always IHC, Non-IHC tab is always non-IHC) */}
                      {filter === "all" && <Table.Th>Type</Table.Th>}
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Priority</Table.Th>
                      {filter === "ihc" && <Table.Th>Stainer</Table.Th>}
                      <Table.Th>Remarks</Table.Th>
                      {showRowActions && <Table.Th>Actions</Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((slide: any) => (
                      <Table.Tr
                        key={slide.id}
                        data-biocare-preflight-error={biocareErrorBySlide.has(
                          slide.id
                        )}
                        bg={
                          biocareErrorBySlide.has(slide.id)
                            ? "var(--mantine-color-red-light)"
                            : undefined
                        }
                      >
                        {/* Checkbox column: show on Non-IHC tab only */}
                        {filter === "non-ihc" && (
                          <Table.Td>
                            <Checkbox
                              checked={selectedSlides.has(slide.id)}
                              onChange={() => toggleSlideSelection(slide.id)}
                            />
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {getDisplayCaseNumber(slide.case) || "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td>{slide.specimen?.number || "N/A"}</Table.Td>
                        <Table.Td>{slide.block?.number || "N/A"}</Table.Td>
                        <Table.Td>{getPhysicalSlideNumber(slide)}</Table.Td>
                        <Table.Td>
                          <Group gap={6}>
                            <Text size="sm">{slide.stain?.code || "N/A"}</Text>
                            {slide.isControlSlide && (
                              <Badge size="xs" variant="light" color="violet">
                                Control
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        {/* Type column: show on All tab only */}
                        {filter === "all" && (
                          <Table.Td>
                            <Badge
                              color={
                                STAIN_TYPE_COLORS[
                                  slide.stain?.stainType ?? ""
                                ] ?? "gray"
                              }
                              variant="light"
                            >
                              {slide.stain?.stainType || "N/A"}
                            </Badge>
                          </Table.Td>
                        )}
                        <Table.Td>
                          <StainingStatusBadge slide={slide} />
                        </Table.Td>
                        <Table.Td>
                          {slide.stainingPriority > 0 ? (
                            <Badge color="red" variant="light">
                              {slide.stainingPriority}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">
                              Normal
                            </Text>
                          )}
                        </Table.Td>
                        {filter === "ihc" && (
                          <Table.Td>{getMachineCell(slide)}</Table.Td>
                        )}
                        <Table.Td>
                          <Stack gap={4}>
                            <Text
                              size="sm"
                              lineClamp={2}
                              title={slide.requestReason}
                            >
                              {slide.requestReason || "—"}
                            </Text>
                            {biocareErrorBySlide.has(slide.id) && (
                              <Text size="xs" c="red.7" fw={600}>
                                {biocareErrorBySlide.get(slide.id)}
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                        {showRowActions && (
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              {filter === "ihc" &&
                                canManuallyCompleteStaining && (
                                  <IhcResendButton
                                    slide={slide}
                                    isPending={resendMutation.isPending}
                                    onResend={handleResendIhcSlide}
                                  />
                                )}
                              {canCompleteRowActions &&
                                isManualMachineCompletionEligible(slide) && (
                                  <Button
                                    size="xs"
                                    variant="light"
                                    color="green"
                                    leftSection={<IconCheck size={14} />}
                                    onClick={() => openManualCompletion(slide)}
                                    loading={
                                      manualCompleteMutation.isPending &&
                                      manualCompletionTarget?.id === slide.id
                                    }
                                    disabled={manualCompleteMutation.isPending}
                                    aria-label={`Mark staining complete for ${
                                      getDisplayCaseNumber(slide.case) || "case"
                                    } slide ${getPhysicalSlideNumber(slide)}`}
                                  >
                                    Mark complete
                                  </Button>
                                )}
                              {canDeleteStain && slide.case?.id && (
                                <DeleteStainButton
                                  slide={slide}
                                  caseId={slide.case.id}
                                  caseNumber={getDisplayCaseNumber(slide.case)}
                                  slideLabel={getPhysicalSlideNumber(slide)}
                                />
                              )}
                              {!canDeleteStain &&
                                !(
                                  canCompleteRowActions &&
                                  isManualMachineCompletionEligible(slide)
                                ) && (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                            </Group>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>
          ))}

        {/* Completed Slides Section */}
        {view === "completed" &&
          (isLoading ? (
            <Loader />
          ) : currentCounts.completed === 0 ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="No Completed Slides"
            >
              No completed slides yet
            </Alert>
          ) : (
            <Paper withBorder p="md">
              <Stack gap="md">
                {/* Pagination above table */}
                <Group justify="space-between">
                  <Text fw={500} size="lg">
                    Completed ({currentCounts.completed})
                  </Text>
                  {totalCompletedPages > 1 && (
                    <Group gap="md">
                      <Text size="sm" c="dimmed">
                        Showing {(completedPage - 1) * ITEMS_PER_PAGE + 1}-
                        {Math.min(
                          completedPage * ITEMS_PER_PAGE,
                          currentCounts.completed
                        )}{" "}
                        of {currentCounts.completed} slides
                      </Text>
                      <Pagination
                        total={totalCompletedPages}
                        value={completedPage}
                        onChange={handleCompletedPageChange}
                        size="sm"
                      />
                    </Group>
                  )}
                </Group>

                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Case Number</Table.Th>
                      <Table.Th>Part</Table.Th>
                      <Table.Th>Block</Table.Th>
                      <Table.Th>Slide</Table.Th>
                      <Table.Th>Stain</Table.Th>
                      {filter === "all" && <Table.Th>Type</Table.Th>}
                      <Table.Th>Remarks</Table.Th>
                      <Table.Th>Completed At</Table.Th>
                      {canDeleteStain && <Table.Th>Actions</Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((slide: any) => (
                      <Table.Tr key={slide.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {getDisplayCaseNumber(slide.case) || "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td>{slide.specimen?.number || "N/A"}</Table.Td>
                        <Table.Td>{slide.block?.number || "N/A"}</Table.Td>
                        <Table.Td>{getPhysicalSlideNumber(slide)}</Table.Td>
                        <Table.Td>
                          <Group gap={6}>
                            <Text size="sm">{slide.stain?.code || "H&E"}</Text>
                            {slide.isControlSlide && (
                              <Badge size="xs" variant="light" color="violet">
                                Control
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        {filter === "all" && (
                          <Table.Td>
                            <Badge
                              color={
                                STAIN_TYPE_COLORS[
                                  slide.stain?.stainType ?? ""
                                ] ?? "gray"
                              }
                              variant="light"
                            >
                              {slide.stain?.stainType || "H&E"}
                            </Badge>
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Text
                            size="sm"
                            lineClamp={2}
                            title={slide.requestReason}
                          >
                            {slide.requestReason || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {slide.stainingCompletedAt
                              ? formatLabInstant(slide.stainingCompletedAt)
                              : "N/A"}
                          </Text>
                        </Table.Td>
                        {canDeleteStain && (
                          <Table.Td>
                            {slide.case?.id ? (
                              <DeleteStainButton
                                slide={slide}
                                caseId={slide.case.id}
                                caseNumber={slide.case?.caseNumber}
                                slideLabel={slide.number}
                              />
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>
          ))}
      </Stack>

      {/* Filter Modal */}
      <FilterModal
        opened={filterModalOpened}
        onClose={closeFilterModal}
        filters={caseFilters}
        onApply={handleFiltersApply}
        allowedStatusNames={["Pending Staining", "Staining"]}
      />

      <Modal
        opened={!!manualCompletionTarget}
        onClose={closeManualCompletion}
        title="Confirm manual IHC/ISH completion"
        centered
        closeOnClickOutside={!manualCompleteMutation.isPending}
        closeOnEscape={!manualCompleteMutation.isPending}
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconAlertCircle size={16} />}>
            Use this only after the slide has physically completed staining.
            This applies the same workflow completion state expected from the
            stainer integration and may advance the case to distribution.
          </Alert>
          <Text size="sm">
            {manualCompletionTarget
              ? `${
                  getDisplayCaseNumber(manualCompletionTarget.case) || "Case"
                } · ${manualCompletionTarget.specimen?.number || "?"}${
                  manualCompletionTarget.block?.number || "?"
                } · Slide ${getPhysicalSlideNumber(manualCompletionTarget)} · ${
                  manualCompletionTarget.stain?.code || "IHC/ISH"
                }`
              : ""}
          </Text>
          <Textarea
            label="Reason or notes (optional)"
            description="Saved in the audit record for this manual override."
            value={manualCompletionNotes}
            onChange={(event) =>
              setManualCompletionNotes(event.currentTarget.value)
            }
            maxLength={1000}
            autosize
            minRows={2}
            disabled={manualCompleteMutation.isPending}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={closeManualCompletion}
              disabled={manualCompleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleManualCompletionConfirm}
              loading={manualCompleteMutation.isPending}
            >
              Confirm completion
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
